import { Bell, Settings } from 'lucide-react';

import {
  useAlertRule,
  useAlertRuleEnvironments,
  AlertRuleEnvironmentsResponse,
} from '@/react/portainer/observability/AlertsView/AlertRulesDatatable/useAlertRules';
import { transformToAlertRuleRow } from '@/react/portainer/observability/AlertsView/AlertRulesDatatable/utils';
import { useIdParam } from '@/react/hooks/useIdParam';
import { AlertRuleInfo } from '@/react/portainer/observability/AlertsView/AlertRuleEdit/AlertRuleInfo';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody, WidgetTabs } from '@@/Widget';
import { Alert } from '@@/Alert';
import { HeaderLayout } from '@@/HeaderLayout/HeaderLayout';
import { useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Badge } from '@@/Badge';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { StackedStatusBar } from '@@/StackedStatusBar';

import { AlertRuleEditForm } from './AlertRuleEditForm';
import { AlertRuleAttachments } from './AlertRuleAttachments';
import { formatAlertRuleStatusTooltip } from './alertRuleStatusTooltip';

function AlertRuleStatusProgressBar({
  environments,
}: {
  environments: AlertRuleEnvironmentsResponse;
}) {
  const { active, error, notSupported, pending } = environments;
  const displayTotal = active + pending + error + notSupported;

  if (displayTotal === 0) {
    return <span className="text-muted text-sm">No environments targeted</span>;
  }

  const tooltipMessage = formatAlertRuleStatusTooltip(environments);

  return (
    <TooltipWithChildren message={tooltipMessage}>
      <div className="flex flex-col items-end gap-1">
        <StackedStatusBar
          className="!w-48"
          steps={[
            { value: active, className: 'bg-success-7', label: 'Active' },
            { value: pending, className: 'bg-warning-7', label: 'In progress' },
            { value: error, className: 'bg-error-7', label: 'Failed' },
            {
              value: notSupported,
              className: 'bg-gray-6',
              label: 'Not supported',
            },
          ]}
          total={displayTotal}
          aria-label="Alert rule attachment status"
        />
      </div>
    </TooltipWithChildren>
  );
}

export function AlertRuleDetails() {
  const ruleId = useIdParam();
  const alertRulesQuery = useAlertRule({
    id: ruleId,
    select: (row) => transformToAlertRuleRow(row),
  });
  const environmentsQuery = useAlertRuleEnvironments(ruleId);
  const showAttachmentsTab =
    alertRulesQuery.data?.labels?.category === 'environment' &&
    !!alertRulesQuery.data?.supportedAgentVersion;
  const tabs = [
    {
      name: 'Info',
      icon: Settings,
      widget: (
        <>
          {alertRulesQuery.data && (
            <AlertRuleInfo alertRule={alertRulesQuery.data} />
          )}
        </>
      ),
      selectedTabParam: 'info',
    },
    {
      name: 'Settings',
      icon: Settings,
      widget: (
        <>
          {alertRulesQuery.data && (
            <AlertRuleEditForm alertRule={alertRulesQuery.data} />
          )}
        </>
      ),
      selectedTabParam: 'settings',
    },
  ];
  if (showAttachmentsTab) {
    tabs.splice(1, 0, {
      name: 'Attachments',
      icon: Settings,
      widget: (
        <>
          {alertRulesQuery.data && (
            <AlertRuleAttachments alertRule={alertRulesQuery.data} />
          )}
        </>
      ),
      selectedTabParam: 'attachments',
    });
  }
  const currentTabIndex = useCurrentTabIndex(tabs);

  return (
    <>
      <PageHeader
        loading={alertRulesQuery.isLoading}
        breadcrumbs={[
          {
            label: 'Alerting',
            link: 'portainer.alerting.index',
            linkParams: { tab: 'active-alerts' },
          },
          {
            label: 'Rules',
            link: 'portainer.alerting.index',
            linkParams: { tab: 'rules' },
          },
          { label: alertRulesQuery.data?.name || 'Loading' },
        ]}
      />
      <div className="row">
        <div className="col-sm-12">
          <HeaderLayout
            isLoading={alertRulesQuery.isLoading}
            icon={<Bell />}
            subtitleLabel="Alert rule"
            title={alertRulesQuery.data?.name ?? ''}
            badge={
              alertRulesQuery.data && (
                <>
                  <Badge type="muted">
                    {alertRulesQuery.data.isInternal ? 'System' : 'Custom'}
                  </Badge>
                  {alertRulesQuery.data.labels?.category && (
                    <Badge type="info">
                      {alertRulesQuery.data.labels.category}
                    </Badge>
                  )}
                </>
              )
            }
            rightInfo={
              environmentsQuery.data && (
                <AlertRuleStatusProgressBar
                  environments={environmentsQuery.data}
                />
              )
            }
          />
        </div>
      </div>

      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />

      <div className="row">
        <div className="col-sm-12">
          <Widget>
            <WidgetBody
              className="no-padding"
              loading={alertRulesQuery.isLoading || !alertRulesQuery.data}
            >
              {alertRulesQuery.isError && (
                <Alert color="error">Error loading rule</Alert>
              )}
              <div className="content">{tabs[currentTabIndex].widget}</div>
            </WidgetBody>
          </Widget>
        </div>
      </div>
    </>
  );
}
